import React, { useEffect, useState } from 'react';
import { Mail, Check } from 'lucide-react';

// EmailPromptModal — used at checkout to capture an email for the payment
// gateway. Client 2026-09-08 (revised):
//   • The email is passed to Curlec so the receipt reaches the customer.
//   • The backend ALSO best-effort saves it to the user's profile — so the
//     modal doesn't appear again on future orders. This is a courtesy
//     auto-save, not a validation: uniqueness collisions (another same-role
//     account already has this email) are silently skipped, payment always
//     proceeds regardless.
//   • The same email can therefore still be typed by multiple users; only
//     the FIRST one to type it "wins" the profile auto-save. The rest still
//     get their receipt from Curlec — just no profile update.
//
// Props:
//   open       — boolean, controls visibility
//   defaultEmail — optional pre-fill (e.g. last-used or profile email)
//   onCancel   — called if the user closes without saving. Payment flow
//                should NOT proceed in that case.
//   onSuccess  — called with the validated email string. Payment flow
//                proceeds from here — caller is responsible for passing
//                the email to createPayment().
//   busy       — parent's own busy flag (disables the buttons while the
//                parent is doing its own async work post-success).
function EmailPromptModal({ open, defaultEmail = '', onCancel, onSuccess, busy = false }) {
  const [email, setEmail] = useState(defaultEmail);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (open) { setEmail(defaultEmail || ''); setErr(''); }
  }, [open, defaultEmail]);

  useEffect(() => {
    if (!open) return;
    function onKey(e) { if (e.key === 'Escape' && !busy) onCancel?.(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onCancel]);

  if (!open) return null;

  const trimmed = email.trim().toLowerCase();
  const valid = /^\S+@\S+\.\S+$/.test(trimmed);

  function submit(e) {
    e?.preventDefault?.();
    if (!valid) { setErr('Please enter a valid email.'); return; }
    setErr('');
    onSuccess?.(trimmed);
  }

  return (
    <div
      className="modal-backdrop"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onCancel?.(); }}
    >
      <div className="modal" role="dialog" aria-modal="true" style={{ maxWidth: 460 }}>
        <div className="modal-head">
          <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Mail size={18} /> One more step
          </h3>
        </div>
        <form onSubmit={submit}>
          <div className="modal-body">
            <p className="muted" style={{ marginTop: 0 }}>
              We need an email address to send your payment receipt and booking
              updates. This is required by the payment gateway.
            </p>
            <div className="field" style={{ marginTop: 12 }}>
              <label>Email address</label>
              <input
                className="input"
                type="email"
                inputMode="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => { setEmail(e.target.value); setErr(''); }}
                placeholder="you@example.com"
                required
              />
            </div>
            {err && <div className="notice warning" style={{ marginTop: 12 }}>{err}</div>}
            <div className="muted text-xs" style={{ marginTop: 12 }}>
              We'll use this email for your receipt and booking updates, and
              save it to your profile so we don't need to ask again next time.
              You can change it any time in Profile settings.
            </div>
          </div>
          <div className="modal-foot">
            <button
              type="button"
              className="btn btn-ghost"
              onClick={onCancel}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!valid || busy}
            >
              {busy ? <span className="spinner" /> : <Check size={16} />}
              {busy ? 'Continuing…' : 'Save & continue'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EmailPromptModal;
